import getWebsiteUrl from '@cc/shared/utils/getWebsiteUrl';

const convertImagesToBase64 = async (element: HTMLElement) => {
    const images = element.querySelectorAll('img') as NodeListOf<HTMLImageElement>;

    for (const img of images) {
        try {
            if (img.src && !img.src.startsWith('data:')) {
                const base64 = await imageToBase64(img.src);
                if (base64) {
                    img.src = base64;
                }
            }
        } catch (e) {
            console.warn('Could not convert image to base64:', e);
        }
    }
};

const imageToBase64 = (src: string): Promise<string | null> => {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                resolve(null);
                return;
            }
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            try {
                const dataURL = canvas.toDataURL('image/png');
                resolve(dataURL);
            } catch (e) {
                console.warn('Canvas conversion failed:', e);
                resolve(null);
            }
        };

        img.onerror = () => resolve(null);
        img.src = src;
    });
};

const extractHTMLForPDF = async (element: HTMLElement) => {
    const clone = element.cloneNode(true) as HTMLElement;

    // Convert images to base64
    await convertImagesToBase64(clone);

    applyPDFOptimizations(clone);
    const cssRules = extractRelevantCSS(element);
    return createCompleteHTML(clone.outerHTML, cssRules);
};

const applyPDFOptimizations = (element: HTMLElement) => {
    const allElements = [element, ...element.querySelectorAll('*')] as HTMLElement[];
    allElements.forEach(el => {
        const textContent = el.textContent || '';
        if (/\d/.test(textContent)) {
            if (!el.style.fontFamily || el.style.fontFamily.includes('system-ui') || el.style.fontFamily.includes('-apple-system')) {
                el.style.fontFamily = "Arial, sans-serif";
            }
        }
        if (el.style.position === 'fixed') {
            el.style.position = 'absolute';
        }
        ['onclick', 'onmouseover', 'onmouseout'].forEach(attr => el.removeAttribute(attr));
    });
};

const extractRelevantCSS = (element: HTMLElement) => {
    const relevantRules = new Set<string>();
    const allElements = [element, ...element.querySelectorAll('*')];

    // Extract inline styles as backup
    const inlineStyles = new Set<string>();
    allElements.forEach(el => {
        if (el instanceof HTMLElement && el.style && el.style.cssText) {
            const selector = el.tagName.toLowerCase() +
                (el.className ? `.${el.className.split(' ').join('.')}` : '') +
                (el.id ? `#${el.id}` : '');
            inlineStyles.add(`${selector} { ${el.style.cssText} }`);
        }
    });

    try {
        for (const styleSheet of document.styleSheets) {
            try {
                const rules = styleSheet.cssRules || styleSheet.rules;
                for (const rule of rules) {
                    if (rule.type === CSSRule.STYLE_RULE) {
                        if (allElements.some(el => {
                            try {
                                return el.matches((rule as CSSStyleRule).selectorText);
                            } catch (e) {
                                return false;
                            }
                        })) {
                            relevantRules.add(rule.cssText);
                        }
                    }
                    // Include @media rules for responsive design
                    else if (rule.type === CSSRule.MEDIA_RULE) {
                        try {
                            const mediaRule = rule as CSSMediaRule;
                            for (const nestedRule of mediaRule.cssRules) {
                                if (nestedRule.type === CSSRule.STYLE_RULE) {
                                    if (allElements.some(el => {
                                        try {
                                            return el.matches((nestedRule as CSSStyleRule).selectorText);
                                        } catch (e) {
                                            return false;
                                        }
                                    })) {
                                        relevantRules.add(rule.cssText);
                                        break;
                                    }
                                }
                            }
                        } catch (e) {
                            // Add the entire media rule if we can't parse it
                            relevantRules.add(rule.cssText);
                        }
                    }
                }
            } catch (e) {
                console.warn('Cannot access stylesheet:', e);
            }
        }
    } catch (e) {
        console.warn('Error extracting CSS:', e);
    }

    // Combine stylesheet rules with inline styles
    const allRules = [...Array.from(relevantRules), ...Array.from(inlineStyles)];
    return allRules.join('\n');
};

const createCompleteHTML = (bodyHTML: string, cssRules = '') => {
    const printStyles = `
        @page {
            size: A4;
            margin: 1.5cm;
        }
        * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            word-wrap: normal;
            white-space: normal;
        }
        p, div, span {
            word-break: normal;
            overflow-wrap: normal;
            hyphens: none;
        }
        span, div, td, th {
            font-variant-numeric: lining-nums;
        }
        img {
            max-width: 100%;
            height: auto;
            display: block;
        }
        .print-button {
            display: none;
        }
    `;

    // More comprehensive text sanitization
    const sanitizedHTML = bodyHTML
        .replace(/(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4})/g,
            (match) => `<span style="font-family: Arial, sans-serif; font-variant-numeric: lining-nums;">${match}</span>`)
        // Fix any problematic Unicode characters
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/[\u2013\u2014]/g, "-")
        .replace(/\u2026/g, "...");

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
    <title>PDF Content</title>
    <style>
        ${cssRules}
    </style>
    <style>
        ${printStyles}
    </style>
</head>
<body>
    ${sanitizedHTML}
</body>
</html>`;
};

const generatePDF = async (element: HTMLElement): Promise<Blob | null> => {
    try {
        const content = await extractHTMLForPDF(element);
        const response = await fetch(getWebsiteUrl({ slug: '/api/embed/generate-pdf/' }), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ content }),
        });
        if (!response.ok) {
            throw new Error(`PDF generation failed: ${response.statusText}`);
        }
        return response.blob();
    } catch (error) {
        console.error('PDF generation failed:', error);
        return null;
    }
};

export default generatePDF;
